import { Loading } from "../app/Status";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState, type FormEvent } from "react";
import { Link } from "react-router";
import {
  ASSET_KIND_FILTER_LABELS,
  CATALOG_CSV_COLUMNS,
  CATALOG_SOURCE_LABELS,
  DEFECT_INTENSITY_LABELS,
  DEFECT_SEVERITY_LABELS,
  DEFECT_TYPE_LABELS,
  DISCIPLINE_LABELS,
  DISCIPLINES,
  ASSESSMENT_STATUS_LABELS,
  CONDITION_SCORE_LABELS,
  CONDITION_SCORES,
  PROPOSAL_ACTION_LABELS,
  PROPOSAL_ACTIONS,
  SAFETY_NET_MAX,
  SCORE_METHOD_LABELS,
  SCORE_METHODS,
  safetyNetOf,
  safetyNetText,
  todayIso,
  type CatalogJsonImportResult,
  type SafetyNetMax,
  type ScoreMethod,
  type ConditionSettingsDto,
  type ProposalAction,
  type ProposalRules,
  type CatalogImportResult,
  type DefectCatalogDto,
  type DefectCatalogItemDto,
  type Discipline,
  type AssessmentDto,
  type AssessmentInstruction,
  type AssessorOption,
} from "@klantpunt/shared";
import { IconPlus, IconSettings, IconUpload } from "../app/icons";
import { Meta } from "../app/Meta";
import { Tabs } from "../app/Choice";
import { PageHeader } from "../app/PageHeader";
import { AssetDialog } from "../features/assets/AssetDialog";
import { useBuilding } from "../features/building/useBuilding";
import { api, ApiError } from "../lib/api";
import { hasModule, useMe } from "../lib/auth";
import { useBuildingContext } from "../lib/buildingContext";
import { useToast } from "../lib/toast";

/**
 * Conditiemeting NEN 2767 (docs/CONDITIEMETING.md): metingen per gebouw, *Meting starten* met de
 * inspectie-instructie (§5.1, §10.1) en de gebrekenlijsten (§6: generiek, eigen lijst, CSV- of JSON-import).
 */
const errMsg = (e: unknown) => (e instanceof ApiError ? e.message : "Er ging iets mis");
const fmtDate = (iso: string) =>
  new Date(`${iso}T12:00:00`).toLocaleDateString("nl-NL", { day: "numeric", month: "short", year: "numeric" });

export function ConditionPage() {
  const { data: me } = useMe();
  const [tab, setTab] = useState<"assessments" | "catalogs" | "settings">("assessments");
  /** "Meting starten" in de paginakop: opent het formulier in Metingen (daarna weer uit). */
  const [newRequest, setNewRequest] = useState(false);
  if (!hasModule(me, "condition"))
    return (
      <div className="center">
        <p>Geen toegang tot de module Conditiemeting.</p>
      </div>
    );
  const canEdit = hasModule(me, "condition", "edit");
  return (
    <div className="page condition">
      <PageHeader
        label="NEN 2767"
        title="Conditiemeting"
        action={
          canEdit && (
            <button
              type="button"
              className="btn primary"
              onClick={() => {
                setTab("assessments");
                setNewRequest(true);
              }}
            >
              <IconPlus size={18} aria-hidden="true" />
              Meting starten
            </button>
          )
        }
        menu={
          <button type="button" className="menuitem" onClick={() => setTab("settings")}>
            <IconSettings size={20} aria-hidden="true" />
            Instellingen
          </button>
        }
        tabs={
          <Tabs
            label="Onderdeel"
            items={[
              { value: "assessments", label: "Metingen" },
              { value: "catalogs", label: "Gebrekenlijsten" },
            ]}
            value={tab === "settings" ? null : tab}
            onChange={setTab}
          />
        }
      />
      {tab === "settings" && <h2>Instellingen</h2>}
      {tab === "assessments" ? (
        <Assessments canEdit={canEdit} newRequest={newRequest} onNewHandled={() => setNewRequest(false)} />
      ) : tab === "catalogs" ? (
        <Catalogs canEdit={canEdit} canImport={canEdit && (me?.role === "owner" || me?.role === "beheerder")} />
      ) : (
        <Settings canEdit={canEdit && (me?.role === "owner" || me?.role === "beheerder")} />
      )}
    </div>
  );
}

// ---------- metingen ----------

function Assessments({
  canEdit,
  newRequest,
  onNewHandled,
}: {
  canEdit: boolean;
  newRequest: boolean;
  onNewHandled: () => void;
}) {
  const ctx = useBuildingContext();
  const buildings = { data: ctx.buildings };
  const buildingId = ctx.id ?? "";
  const setBuildingId = (v: string) => ctx.setBuilding(v || null);
  const [editing, setEditing] = useState<AssessmentDto | "new" | null>(null);
  const bid = buildingId || buildings.data?.[0]?.id || "";
  useEffect(() => {
    if (!newRequest) return;
    if (canEdit) setEditing("new");
    onNewHandled();
  }, [newRequest, canEdit, onNewHandled]);
  const key = ["condition-assessments", bid];
  const list = useQuery({
    queryKey: key,
    queryFn: () => api<AssessmentDto[]>(`/buildings/${bid}/condition/assessments`),
    enabled: Boolean(bid),
  });
  if (buildings.data && !buildings.data.length) return <p className="muted">Nog geen gebouwen.</p>;
  return (
    <>
      <div className="mjop-filters">
        <label className="field">
          <span className="label">Gebouw</span>
          <select
            value={bid}
            onChange={(e) => {
              setBuildingId(e.target.value);
              setEditing(null);
            }}
          >
            {buildings.data?.map((b) => (
              <option key={b.id} value={b.id}>
                {b.code} · {b.name}
              </option>
            ))}
          </select>
        </label>
      </div>
      {editing && bid ? (
        <AssessmentForm
          key={editing === "new" ? "new" : editing.id}
          buildingId={bid}
          assessment={editing === "new" ? null : editing}
          canEdit={canEdit}
          onClose={() => setEditing(null)}
        />
      ) : (
        <section className="panel" aria-label="Metingen">
          {list.isPending ? (
            <Loading />
          ) : !list.data?.length ? (
            <p className="muted">
              Nog geen metingen voor dit gebouw.
              {canEdit && " Start een meting om de inspectie-instructie op te stellen."}
            </p>
          ) : (
            <ul className="list">
              {list.data.map((m) => (
                <li key={m.id} className="profile-row">
                  <span className="grow">
                    <strong>{m.assessedOn ? fmtDate(m.assessedOn) : "Datum nog niet gekozen"}</strong>
                    <Meta
                      className="muted small"
                      items={[
                        ASSESSMENT_STATUS_LABELS[m.status],
                        m.assessor && (m.assessor.name ?? "inspecteur"),
                        m.defectCatalog && `${m.defectCatalog.name} (${m.defectCatalog.version})`,
                        m.disciplines.map((d) => DISCIPLINE_LABELS[d]).join(", "),
                        safetyNetOf(m.instruction) != null && safetyNetText(safetyNetOf(m.instruction)),
                      ]}
                    />
                  </span>
                  <span className="btnrow">
                    {(m.status === "in_progress" || m.status === "completed") && (
                      <Link to={`/conditie/meting/${m.id}`} className="btn small">
                        {m.status === "in_progress" && canEdit ? "Meting uitvoeren" : "Resultaat"}
                      </Link>
                    )}
                    <button type="button" className="btn small" onClick={() => setEditing(m)}>
                      {canEdit && m.status === "draft" ? "Openen" : "Instructie"}
                    </button>
                  </span>
                </li>
              ))}
            </ul>
          )}
        </section>
      )}
    </>
  );
}

const INSTRUCTION_FIELDS: { k: keyof AssessmentInstruction; label: string; hint: string; rows: number }[] = [
  { k: "scope", label: "Scope", hint: "Wat wordt wel en niet beoordeeld (bijv. alleen de gebouwschil).", rows: 3 },
  {
    k: "ownAssets",
    label: "Wat is een eigen asset?",
    hint: "Welke delen een eigen asset zijn en wat als component bij een asset hoort, bijv. raamdorpels horen bij de kozijnen. Dit bepaalt de ernst van veroudering en verandert niet tijdens de meting.",
    rows: 3,
  },
  { k: "detailLevel", label: "Detailniveau", hint: "Bijv. per gevelvlak of per kozijn.", rows: 2 },
  { k: "layout", label: "Indeling", hint: "Gevels, dakvlakken, verdiepingen.", rows: 2 },
  { k: "structuralCracks", label: "Aanpak constructieve scheuren", hint: "", rows: 2 },
  { k: "lifespanSource", label: "Bron levensduren", hint: "Bijv. kennisbank Klantpunt of eigen opgave.", rows: 1 },
];

function AssessmentForm({
  buildingId,
  assessment: m,
  canEdit,
  onClose,
}: {
  buildingId: string;
  assessment: AssessmentDto | null;
  canEdit: boolean;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const b = useBuilding(buildingId);
  const assessors = useQuery({
    queryKey: ["condition-assessors"],
    queryFn: () => api<AssessorOption[]>("/condition/assessors"),
  });
  const catalogs = useQuery({
    queryKey: ["condition-catalogs"],
    queryFn: () => api<DefectCatalogDto[]>("/condition/catalogs"),
  });
  const [id] = useState(() => m?.id ?? crypto.randomUUID());
  const [date, setDate] = useState(m?.assessedOn ?? todayIso());
  const [assessor, setAssessor] = useState(m?.assessor?.id ?? "");
  const [disc, setDisc] = useState<Discipline[]>(m?.disciplines ?? ["B"]);
  const [catalogId, setCatalogId] = useState(m?.defectCatalog?.id ?? "");
  // Nieuwe meting: zet de eigen gebrekenlijst klaar (een lijst met bouwdelen gaat voor).
  const pickedCatalog = useRef(!!m);
  useEffect(() => {
    if (pickedCatalog.current || !catalogs.data) return;
    pickedCatalog.current = true;
    const own = catalogs.data.filter((c) => c.orgId && c.active);
    // Een specifieke lijst (NEN of eigen import) gaat voor de algemene Klantpunt-lijst.
    const withParts = own.filter((c) => c.partCount > 0);
    const pick =
      withParts.find((c) => c.source === "nen2767-2") ??
      withParts.find((c) => c.name !== STANDARD_CATALOG_NAME) ??
      withParts[0] ??
      (own.length === 1 ? own[0] : undefined);
    if (pick) setCatalogId(pick.id);
  }, [catalogs.data]);
  const [ins, setIns] = useState<AssessmentInstruction>(
    m?.instruction ?? { excludedAssetIds: [], safetyNet: false, method: "table5" },
  );
  const [addAsset, setAddAsset] = useState(false);
  // Na de eerste keer opslaan bestaat het concept: daarna bijwerken, ook als starten mislukte.
  const [created, setCreated] = useState(!!m);
  const status = m?.status ?? "draft";
  const editable = canEdit && (status === "draft" || status === "in_progress");
  const catalogFixed = status !== "draft";
  const key = ["condition-assessments", buildingId];

  const body = () => ({
    assessedOn: date || null,
    assessorUserId: assessor || null,
    disciplines: disc,
    ...(catalogFixed ? {} : { defectCatalogId: catalogId || null }),
    instruction: ins,
  });
  const save = useMutation({
    mutationFn: async (start: boolean) => {
      let r = created
        ? await api<AssessmentDto>(`/condition/assessments/${id}`, { method: "PATCH", body: body() })
        : await api<AssessmentDto>(`/buildings/${buildingId}/condition/assessments`, {
            method: "POST",
            body: { id, ...body() },
          });
      setCreated(true);
      if (start) r = await api<AssessmentDto>(`/condition/assessments/${id}/start`, { method: "POST" });
      return r;
    },
    onSuccess: (r, start) => {
      void qc.invalidateQueries({ queryKey: key });
      toast(start ? "Meting gestart" : "Meting opgeslagen");
      if (start || r.status !== "draft") onClose();
    },
    onError: (e) => toast(errMsg(e)),
  });
  const act = useMutation({
    mutationFn: (what: "cancel" | "delete") =>
      what === "delete"
        ? api(`/condition/assessments/${id}`, { method: "DELETE" })
        : api(`/condition/assessments/${id}/cancel`, { method: "POST" }),
    onSuccess: (_r, what) => {
      void qc.invalidateQueries({ queryKey: key });
      toast(what === "delete" ? "Concept verwijderd" : "Meting geannuleerd");
      onClose();
    },
    onError: (e) => toast(errMsg(e)),
  });
  // Arbeidsmiddelen en meters doen niet mee aan de conditiemeting (docs/METERSTANDEN.md).
  const assets = (b.data?.assets ?? []).filter((a) => a.assetKind !== "equipment" && a.assetKind !== "meter");
  const counts = { installation: 0, building_part: 0, equipment: 0, meter: 0 };
  for (const a of assets) counts[a.assetKind]++;
  const submit = (e: FormEvent) => {
    e.preventDefault();
    save.mutate(false);
  };

  return (
    <form className="panel stack" aria-label="Meting starten" onSubmit={submit}>
      <div className="titlerow">
        <h2>{m ? `Meting ${ASSESSMENT_STATUS_LABELS[status].toLowerCase()}` : "Meting starten"}</h2>
        <button type="button" className="btn ghost" onClick={onClose}>
          Terug
        </button>
      </div>
      <fieldset className="formgrid" disabled={!editable}>
        <label className="field">
          <span className="label">Datum veldinspectie</span>
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
        </label>
        <label className="field">
          <span className="label">Inspecteur</span>
          <select value={assessor} onChange={(e) => setAssessor(e.target.value)}>
            <option value="">Kies…</option>
            {assessors.data?.map((u) => (
              <option key={u.id} value={u.id}>
                {u.name ?? u.email}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          <span className="label">Gebrekenlijst</span>
          <select
            value={catalogId}
            disabled={catalogFixed}
            title={catalogFixed ? "Ligt vast sinds de start van de meting" : undefined}
            onChange={(e) => setCatalogId(e.target.value)}
          >
            <option value="">Generieke gebreken</option>
            {catalogs.data
              ?.filter((c) => c.orgId && (c.active || c.id === catalogId))
              .map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name} ({c.version})
                </option>
              ))}
          </select>
        </label>
      </fieldset>
      <fieldset className="mjop-types" disabled={!editable}>
        <legend className="label">Disciplines</legend>
        {DISCIPLINES.map((d) => (
          <label key={d} className="chip toggle">
            <input
              type="checkbox"
              checked={disc.includes(d)}
              onChange={(e) => setDisc(e.target.checked ? [...disc, d] : disc.filter((x) => x !== d))}
            />
            {d} · {DISCIPLINE_LABELS[d]}
          </label>
        ))}
      </fieldset>

      <fieldset className="stack" disabled={!editable} aria-label="Inspectie-instructie">
        <legend className="label">Inspectie-instructie</legend>
        {INSTRUCTION_FIELDS.map((f) => (
          <label key={f.k} className="field">
            <span className="label">{f.label}</span>
            <textarea
              rows={f.rows}
              value={(ins[f.k] as string | null | undefined) ?? ""}
              onChange={(e) => setIns({ ...ins, [f.k]: e.target.value })}
            />
            {f.hint && <span className="hint">{f.hint}</span>}
          </label>
        ))}
        <label className="field">
          <span className="label">Vangnetconstructie (verval op leeftijd)</span>
          <select
            value={safetyNetOf(ins) ?? ""}
            onChange={(e) => {
              const max = e.target.value ? (Number(e.target.value) as SafetyNetMax) : null;
              setIns({ ...ins, safetyNetMax: max, safetyNet: max != null });
            }}
          >
            <option value="">Uit</option>
            {SAFETY_NET_MAX.map((n) => (
              <option key={n} value={n}>
                Aan, verval tot en met conditie {n}
              </option>
            ))}
          </select>
          <span className="hint">
            Voor delen die je niet kunt zien (ingestort, achter een plafond). Is een asset ouder dan 50%, 75% of 87,5%
            van zijn levensduur, dan stelt de app verval voor: een gering gebrek in begin-, gevorderd of eindstadium,
            over het deel dat je niet kunt beoordelen. Alleen voor assets met bouwjaar en levensduur.
          </span>
        </label>
        <label className="field">
          <span className="label">Bij één gebrek</span>
          <select
            value={ins.method ?? "table5"}
            onChange={(e) => setIns({ ...ins, method: e.target.value as ScoreMethod })}
          >
            {SCORE_METHODS.map((x) => (
              <option key={x} value={x}>
                {SCORE_METHOD_LABELS[x]}
              </option>
            ))}
          </select>
          <span className="hint">
            De norm laat je vooraf kiezen wat leidend is als een asset één soort gebrek heeft. Met meer soorten rekent
            de app altijd met de correctiefactoren uit bijlage B.
          </span>
        </label>
      </fieldset>

      <section className="stack" aria-label="Assets in deze meting">
        <p className="label">Assets in deze meting</p>
        <p className="small muted">
          {assets.length - ins.excludedAssetIds.length} van {assets.length} assets ·{" "}
          {ASSET_KIND_FILTER_LABELS.installation} {counts.installation} · {ASSET_KIND_FILTER_LABELS.building_part}{" "}
          {counts.building_part}. Controleer of alle delen die beoordeeld moeten worden als asset bestaan, ook
          bouwkundige zoals dakbedekking en gevels.
        </p>
        {assets.length > 0 && (
          <fieldset className="checklist" disabled={!editable}>
            <legend className="sr-only">Assets beoordelen</legend>
            {assets.map((a) => (
              <label key={a.id}>
                <input
                  type="checkbox"
                  checked={!ins.excludedAssetIds.includes(a.id)}
                  onChange={(e) =>
                    setIns({
                      ...ins,
                      excludedAssetIds: e.target.checked
                        ? ins.excludedAssetIds.filter((x) => x !== a.id)
                        : [...ins.excludedAssetIds, a.id],
                    })
                  }
                />
                <span>
                  {a.code} {a.name}
                  <span className="muted small"> · {ASSET_KIND_FILTER_LABELS[a.assetKind].toLowerCase()}</span>
                </span>
              </label>
            ))}
          </fieldset>
        )}
        {editable && b.data && (
          <div>
            <button type="button" className="btn small" onClick={() => setAddAsset(true)}>
              <IconPlus size={16} aria-hidden="true" />
              Asset toevoegen
            </button>
          </div>
        )}
      </section>

      {editable && (
        <div className="btnrow">
          <button type="submit" className="btn" disabled={save.isPending}>
            Opslaan
          </button>
          {status === "draft" && (
            <button type="button" className="btn primary" disabled={save.isPending} onClick={() => save.mutate(true)}>
              Meting starten
            </button>
          )}
          <span className="grow" />
          {m && status === "draft" && (
            <button type="button" className="btn ghost" onClick={() => act.mutate("delete")}>
              Concept verwijderen
            </button>
          )}
          {m && status === "in_progress" && (
            <button
              type="button"
              className="btn ghost"
              onClick={() => {
                if (confirm("Deze meting annuleren? Hij blijft zichtbaar in de historie.")) act.mutate("cancel");
              }}
            >
              Meting annuleren
            </button>
          )}
        </div>
      )}
      {addAsset && b.data && (
        <AssetDialog
          building={b.data}
          initial={{ assetKind: "building_part", floorId: b.data.floors[0]?.id ?? null }}
          onClose={() => setAddAsset(false)}
        />
      )}
    </form>
  );
}

// ---------- gebrekenlijsten ----------

/**
 * Gebrekenlijst uit een JSON-bestand (§6.3), bijv. de NEN 2767-2-lijsten voor een organisatie met
 * licentie. Eerst een voorbeeld, daarna een nieuwe lijst met bouwdelen.
 */
const STANDARD_CATALOG_NAME = "Klantpunt-gebrekenlijst";

/** Klantpunt-gebrekenlijst: veelvoorkomende gebreken per bouwdeel, eigen inhoud zonder NEN 2767-2. */
function StandardCatalog({ onDone }: { onDone: (id: string) => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const add = useMutation({
    mutationFn: () => api<CatalogJsonImportResult>("/condition/catalogs/standard", { method: "POST" }),
    onSuccess: (r) => {
      void qc.invalidateQueries({ queryKey: ["condition-catalogs"] });
      if (r.catalogId) onDone(r.catalogId);
      toast(`${r.name} klaargezet: ${r.parts} bouwdelen, ${r.defects} gebreken`);
    },
    onError: (e) => toast(errMsg(e)),
  });
  return (
    <div className="stack" aria-label="Klantpunt-gebrekenlijst">
      <p className="label">Klantpunt-gebrekenlijst</p>
      <p className="hint">
        De meest voorkomende gebreken per bouwdeel, bouwkundig en installaties. Je krijgt hem als eigen lijst en kunt
        hem daarna aanpassen. Bij een meting stelt Klantpunt het bouwdeel voor op de NL-SfB-code van de asset.
      </p>
      <div>
        <button type="button" className="btn small" disabled={add.isPending} onClick={() => add.mutate()}>
          <IconPlus size={16} aria-hidden="true" />
          Klantpunt-lijst toevoegen
        </button>
      </div>
    </div>
  );
}

function JsonImport({ onDone }: { onDone: (id: string) => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [data, setData] = useState<unknown>(null);
  const [file, setFile] = useState("");
  const [preview, setPreview] = useState<CatalogJsonImportResult | null>(null);
  const run = useMutation({
    mutationFn: (dryRun: boolean) =>
      api<CatalogJsonImportResult>("/condition/catalogs/import-json", { method: "POST", body: { data, dryRun } }),
    onSuccess: (r, dryRun) => {
      setPreview(r);
      if (!dryRun && r.catalogId) {
        void qc.invalidateQueries({ queryKey: ["condition-catalogs"] });
        onDone(r.catalogId);
        setData(null);
        setFile("");
        setPreview(null);
        toast(`${r.name} geïmporteerd: ${r.parts} bouwdelen, ${r.defects} gebreken`);
      }
    },
    onError: (e) => toast(errMsg(e)),
  });
  return (
    <div className="stack" aria-label="Gebrekenlijst importeren">
      <p className="label">Gebrekenlijst importeren (JSON)</p>
      <p className="hint">
        Voor een lijst met bouwdelen, zoals de gebrekenlijsten van NEN 2767-2 als je organisatie daar een licentie voor
        heeft. De lijst is alleen zichtbaar in deze organisatie.
      </p>
      <div className="btnrow">
        <label className="btn small">
          <IconUpload size={16} aria-hidden="true" />
          {file || "Bestand kiezen"}
          <input
            type="file"
            accept=".json,application/json"
            hidden
            onChange={async (e) => {
              const f = e.target.files?.[0];
              e.target.value = "";
              if (!f) return;
              setPreview(null);
              try {
                const parsed: unknown = JSON.parse(await f.text());
                setData(parsed);
                setFile(f.name);
                run.mutate(true);
              } catch {
                toast("Dit is geen geldig JSON-bestand");
              }
            }}
          />
        </label>
        {preview && !preview.errors.length && (
          <button type="button" className="btn small" disabled={run.isPending} onClick={() => run.mutate(false)}>
            {run.isPending ? "Bezig met importeren…" : "Importeren"}
          </button>
        )}
      </div>
      {preview && (
        <div className="note" role="status">
          {preview.errors.length ? (
            <>
              <b>Er is niets opgeslagen:</b>
              <ul>
                {preview.errors.slice(0, 20).map((er) => (
                  <li key={er}>{er}</li>
                ))}
              </ul>
            </>
          ) : (
            <b>
              {preview.name} (versie {preview.version}): {preview.parts} bouwdelen en {preview.defects} gebreken klaar
              om te importeren.
            </b>
          )}
        </div>
      )}
    </div>
  );
}

function Catalogs({ canEdit, canImport }: { canEdit: boolean; canImport: boolean }) {
  const qc = useQueryClient();
  const toast = useToast();
  const key = ["condition-catalogs"];
  const q = useQuery({ queryKey: key, queryFn: () => api<DefectCatalogDto[]>("/condition/catalogs") });
  const [sel, setSel] = useState<string | null>(null);
  const [name, setName] = useState("");
  const create = useMutation({
    mutationFn: () => api<DefectCatalogDto>("/condition/catalogs", { method: "POST", body: { name, version: "1" } }),
    onSuccess: (c) => {
      void qc.invalidateQueries({ queryKey: key });
      setName("");
      setSel(c.id);
      toast("Lijst aangemaakt");
    },
    onError: (e) => toast(errMsg(e)),
  });
  const current = q.data?.find((c) => c.id === (sel ?? q.data?.[0]?.id));
  return (
    <div className="grid-side">
      <section className="panel" aria-label="Lijsten">
        <ul className="list">
          {q.data?.map((c) => (
            <li key={c.id}>
              <button
                type="button"
                className="profile-row linkrow"
                aria-current={c.id === current?.id ? "true" : undefined}
                onClick={() => setSel(c.id)}
              >
                <span className="grow">
                  <strong>{c.name}</strong>
                  <span className="muted small block">
                    {CATALOG_SOURCE_LABELS[c.source]} · versie {c.version} · {c.itemCount} gebreken
                    {c.partCount > 0 && ` · ${c.partCount} bouwdelen`}
                    {c.orgId && c.locked && " · in gebruik"}
                    {!c.active && " · uit"}
                  </span>
                </span>
              </button>
            </li>
          ))}
        </ul>
        <div className="stack">
          {canEdit && (
            <form
              className="row"
              onSubmit={(e) => {
                e.preventDefault();
                if (name.trim()) create.mutate();
              }}
            >
              <label className="field grow">
                <span className="label">Nieuwe eigen lijst</span>
                <input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="bijv. Lijst inspectiebureau"
                />
              </label>
              <button type="submit" className="btn" disabled={!name.trim() || create.isPending}>
                Aanmaken
              </button>
            </form>
          )}
          {canImport && <StandardCatalog onDone={setSel} />}
          {canImport && <JsonImport onDone={setSel} />}
        </div>
      </section>
      {current && <CatalogDetail key={current.id} catalog={current} canEdit={canEdit} onSelect={setSel} />}
    </div>
  );
}

function CatalogDetail({
  catalog: c,
  canEdit,
  onSelect,
}: {
  catalog: DefectCatalogDto;
  canEdit: boolean;
  onSelect: (id: string) => void;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const key = ["condition-catalog-items", c.id];
  const items = useQuery({
    queryKey: key,
    queryFn: () => api<DefectCatalogItemDto[]>(`/condition/catalogs/${c.id}/items`),
  });
  const [csv, setCsv] = useState("");
  const [mode, setMode] = useState<"append" | "replace">("append");
  const [preview, setPreview] = useState<CatalogImportResult | null>(null);
  const own = Boolean(c.orgId);
  const canChange = canEdit && own && !c.locked;
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: key });
    void qc.invalidateQueries({ queryKey: ["condition-catalogs"] });
  };
  const run = useMutation({
    mutationFn: (dryRun: boolean) =>
      api<CatalogImportResult>(`/condition/catalogs/${c.id}/import`, { method: "POST", body: { csv, mode, dryRun } }),
    onSuccess: (r, dryRun) => {
      setPreview(r);
      if (!dryRun && r.written) {
        refresh();
        setCsv("");
        setPreview(null);
        toast(`${r.written} gebreken geïmporteerd`);
      }
    },
    onError: (e) => toast(errMsg(e)),
  });
  const version = useMutation({
    mutationFn: (v: string) =>
      api<DefectCatalogDto>(`/condition/catalogs/${c.id}/versions`, { method: "POST", body: { version: v } }),
    onSuccess: (n) => {
      refresh();
      onSelect(n.id);
      toast(`Versie ${n.version} aangemaakt`);
    },
    onError: (e) => toast(errMsg(e)),
  });
  return (
    <section className="panel stack" aria-label={`Gebrekenlijst ${c.name}`}>
      <div className="titlerow">
        <h2>
          {c.name} <span className="muted small">versie {c.version}</span>
        </h2>
        {canEdit && own && (
          <button
            type="button"
            className="btn small"
            onClick={() => {
              const v = prompt("Nummer of naam van de nieuwe versie", String(new Date().getFullYear()));
              if (v?.trim()) version.mutate(v.trim());
            }}
          >
            Nieuwe versie
          </button>
        )}
      </div>
      {!own && (
        <p className="small muted">
          Generieke gebreken uit de methodiek van NEN 2767, voor elke asset. De gebrekenlijsten van NEN 2767-2 zitten
          niet in Klantpunt: heeft je organisatie een licentie, dan importeer je ze als JSON. Je kunt ook een eigen
          lijst aanmaken.
        </p>
      )}
      {own && c.locked && (
        <p className="small">
          Deze lijst is gebruikt in een gestarte meting en ligt vast. Maak een nieuwe versie om hem te wijzigen.
        </p>
      )}
      <div className="tscroll">
        <table className="it">
          <thead>
            <tr>
              <th>Bouwdeel</th>
              <th>Code</th>
              <th>Gebrek</th>
              <th>Soort</th>
              <th>Ernst</th>
              <th>Intensiteit</th>
            </tr>
          </thead>
          <tbody>
            {items.data?.map((i) => (
              <tr key={i.id}>
                <td>{[i.partCode, i.partName].filter(Boolean).join(" ") || "Elke asset"}</td>
                <td>{i.defectCode ?? "—"}</td>
                <td>{i.description}</td>
                <td>{i.defectCategory ?? DEFECT_TYPE_LABELS[i.defectType]}</td>
                <td>{i.severity ? DEFECT_SEVERITY_LABELS[i.severity] : "door inspecteur"}</td>
                <td>{i.requiredIntensity ? DEFECT_INTENSITY_LABELS[i.requiredIntensity] : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {canChange && (
        <form
          className="stack"
          aria-label="CSV importeren"
          onSubmit={(e) => {
            e.preventDefault();
            run.mutate(true);
          }}
        >
          <p className="label">CSV importeren</p>
          <p className="hint">
            Kolommen (puntkomma of komma): {CATALOG_CSV_COLUMNS.join(", ")}. Ernst mag ook gering, serieus of ernstig
            zijn. Eerst zie je een voorbeeld; pas daarna wordt er iets opgeslagen.
          </p>
          <label className="btn small">
            <IconUpload size={16} aria-hidden="true" />
            Bestand kiezen
            <input
              type="file"
              accept=".csv,text/csv"
              hidden
              onChange={async (e) => {
                const f = e.target.files?.[0];
                if (f) {
                  setCsv(await f.text());
                  setPreview(null);
                }
              }}
            />
          </label>
          <label className="field">
            <span className="label">Inhoud</span>
            <textarea
              rows={5}
              value={csv}
              onChange={(e) => {
                setCsv(e.target.value);
                setPreview(null);
              }}
            />
          </label>
          <label className="field">
            <span className="label">Bestaande regels</span>
            <select value={mode} onChange={(e) => setMode(e.target.value as "append" | "replace")}>
              <option value="append">Behouden en aanvullen</option>
              <option value="replace">Vervangen</option>
            </select>
          </label>
          {preview && (
            <div className="note" role="status">
              {preview.errors.length ? (
                <>
                  <b>{preview.errors.length} fout(en), er is niets opgeslagen.</b>
                  <ul>
                    {preview.errors.slice(0, 20).map((er) => (
                      <li key={er.line}>
                        Regel {er.line}: {er.message}
                      </li>
                    ))}
                  </ul>
                </>
              ) : (
                <b>{preview.items.length} gebreken klaar om te importeren.</b>
              )}
            </div>
          )}
          <div className="btnrow">
            <button type="submit" className="btn" disabled={!csv.trim() || run.isPending}>
              Voorbeeld
            </button>
            <button
              type="button"
              className="btn primary"
              disabled={!preview || preview.errors.length > 0 || run.isPending}
              onClick={() => run.mutate(false)}
            >
              Importeren
            </button>
          </div>
        </form>
      )}
    </section>
  );
}

// ---------- instellingen: voorsteltabel (§8) ----------

function Settings({ canEdit }: { canEdit: boolean }) {
  const qc = useQueryClient();
  const toast = useToast();
  const q = useQuery({
    queryKey: ["condition-settings"],
    queryFn: () => api<ConditionSettingsDto>("/org/condition-settings"),
  });
  const [rules, setRules] = useState<ProposalRules | null>(null);
  const cur = rules ?? q.data?.proposalRules;
  const save = useMutation({
    mutationFn: (proposalRules: ProposalRules) =>
      api<ConditionSettingsDto>("/org/condition-settings", { method: "PUT", body: { proposalRules } }),
    onSuccess: (r) => {
      qc.setQueryData(["condition-settings"], r);
      setRules(null);
      toast("Voorsteltabel opgeslagen");
    },
    onError: (e) => toast(errMsg(e)),
  });
  if (!cur) return <Loading />;
  const set = (k: keyof ProposalRules, action: ProposalAction, years?: number) =>
    setRules({ ...cur, [k]: action === "within" ? { action, years: years ?? cur[k].years ?? 1 } : { action } });
  return (
    <form
      className="panel stack"
      aria-label="Voorsteltabel MJOP"
      onSubmit={(e: FormEvent) => {
        e.preventDefault();
        save.mutate(cur);
      }}
    >
      <h2>Voorstellen voor het MJOP</h2>
      <p className="hint">
        Na afronden van een meting stelt Klantpunt per vervangingspost een ander jaar voor. Bij &ldquo;later&rdquo;
        wordt het bouwjaar plus levensduur, maar niet eerder dan over 5 jaar. Het MJOP verandert pas na overnemen.
      </p>
      <fieldset className="stack" disabled={!canEdit}>
        {CONDITION_SCORES.map((n) => {
          const k = String(n) as keyof ProposalRules;
          const r = cur[k];
          return (
            <div key={n} className="row">
              <span className="grow">
                <strong>{n}</strong> {CONDITION_SCORE_LABELS[n]}
              </span>
              <label className="field">
                <span className="sr-only">Voorstel bij score {n}</span>
                <select value={r.action} onChange={(e) => set(k, e.target.value as ProposalAction)}>
                  {PROPOSAL_ACTIONS.map((a) => (
                    <option key={a} value={a}>
                      {PROPOSAL_ACTION_LABELS[a]}
                    </option>
                  ))}
                </select>
              </label>
              {r.action === "within" && (
                <label className="field">
                  <span className="sr-only">Aantal jaren bij score {n}</span>
                  <input
                    type="number"
                    min={1}
                    max={30}
                    value={r.years ?? 1}
                    onChange={(e) => set(k, "within", Math.max(1, Math.min(30, Number(e.target.value) || 1)))}
                  />
                </label>
              )}
            </div>
          );
        })}
      </fieldset>
      {canEdit ? (
        <div>
          <button type="submit" className="btn primary" disabled={!rules || save.isPending}>
            Opslaan
          </button>
        </div>
      ) : (
        <p className="hint">Alleen een eigenaar of beheerder past de voorsteltabel aan.</p>
      )}
    </form>
  );
}
